import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as productionService from '../../../../services/productionService';
import * as imageService from '../../../../services/imageService';
import * as categoryService from '../../../../services/categoryService';
import classes from './MovieEditor.scss';
import EditorSection from './EditorSection';
import InputList from './InputList';
import Control from './Control';
import TextEditorSection from './TextEditorSection';
import TextAreaEditorSection from './TextAreaEditorSection';
import SelectEditorSection from './SelectEditorSection';
import ImageBrowser from '../ImageBrowser';
import MovieThumbnail from '../../../../components/MovieThumbnail';
import Spinner from '../../../../components/Spinner';

class MovieEditor extends React.Component {
  static propTypes = {
    match: PropTypes.object,
    token: PropTypes.string
  };

  state = {
    loading: true,
    error: null,
    movieData: null,
    categories: [],
    imageData: { thumb: '', full: '' },   // add: guard defaults
    imageBrowser: false,                  // add: controls ImageBrowser
    lang: { title: 'eng' }                // add: used by EditorSection
  };

  _isMounted = false;

  getRouteKey = () =>
    this.props.match?.params?.movieKey ||
    this.props.match?.params?.id ||
    this.props.match?.params?.key ||
    null;

  constructor(props) {
    super(props);
    // basic change handlers so inputs work (avoid empty UI)
    this.changeHandlers = {
      input: {
        title: (val) => this._isMounted && this.setState(prev => ({
          movieData: { ...prev.movieData, title: typeof val === 'object' ? val : { eng: val, fin: prev.movieData?.title?.fin || '' } }
        })),
        category: (val) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, category: val } })),
        year: (val) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, year: val } })),
        runtime: (val) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, runtime: val } })),
        synopsis: (val) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, synopsis: val } })),
      },
      people: {
        cast: (list) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, people: { ...(prev.movieData?.people||{}), cast: list } } })),
        crew: (list) => this._isMounted && this.setState(prev => ({ movieData: { ...prev.movieData, people: { ...(prev.movieData?.people||{}), crew: list } } })),
      },
      lang: {
        title: (lang) => this._isMounted && this.setState(prev => ({ lang: { ...prev.lang, title: lang } }))
      }
    };

    // image browser handlers
    this.activateImageBrowser = () => this.setState({ imageBrowser: true });
    this.deactivateImageBrowser = () => this.setState({ imageBrowser: false });
    this.onSelectImage = (key) => {
      this.setState(prev => ({
        imageBrowser: false,
        movieData: { ...prev.movieData, image: key }
      }), async () => {
        try {
          const imageData = await imageService.fetchImage(key);
          if (this._isMounted && imageData) this.setState({ imageData });
        } catch (e) {}
      });
    };

    // submit/cancel stubs (avoid undefined handlers)
    this.onSubmit = async (e) => {
      e?.preventDefault?.();
      try {
        this.setState({ loading: true });
        const key = this.getRouteKey();
        // build payload from state so PHP receives all fields
        const payload = { ...(this.state.movieData || {}), _key: key || this.state.movieData?._key || this.state.movieData?.id };
        const updated = await productionService.submitMovie?.(this.props.token, payload);
        if (this._isMounted && updated) this.setState({ movieData: updated, loading: false });
        else this.setState({ loading: false });
      } catch (err) {
        console.error('Submit failed:', err, err?.response?.data);
        this._isMounted && this.setState({ loading: false, error: err });
      }
    };
    this.onCancel = () => this.props.history?.goBack?.();
  }

  componentDidMount() {
    this._isMounted = true;
    this.load();
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.match?.params?.movieKey || prevProps.match?.params?.id) !== this.getRouteKey()) {
      this.load();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async load() {
    const key = this.getRouteKey();
    this._isMounted && this.setState({ loading: true, error: null });

    try {
      let movie = null;

      // try redux cache if available
      const list = this.props.production?.items || this.props.production || [];
      if (Array.isArray(list)) {
        movie = list.find(m => m._key === key || m.key === key) || null;
      }

      // fetch if not in cache
      if (!movie && key) {
        movie = await productionService.fetchMovie(key);
      }

      if (!movie) throw new Error('Movie not found: ' + key);

      // fetch image + categories
      const [imageData, categories] = await Promise.all([
        movie.image ? imageService.fetchImage(movie.image) : Promise.resolve({ thumb: '', full: '' }),
        categoryService.fetchCategories?.(this.state.lang.title || 'fin', 'singular') || Promise.resolve({})
      ]);

      if (this._isMounted) this.setState({
        movieData: movie,
        imageData: imageData || { thumb: '', full: '' },
        categories: categories || {},
        loading: false
      });
    } catch (err) {
      console.error('Admin MovieEditor load failed:', err);
      if (this._isMounted) this.setState({ error: err, loading: false });
    }
  }

  render() {
    const key = this.getRouteKey();
    const { loading, error, movieData, imageData } = this.state;

    if (loading) return <div style={{padding:16}}>Loading… ({key})</div>;
    if (error) return <pre style={{padding:16,color:'crimson'}}>Error: {error.message}</pre>;
    if (!movieData) return <div style={{padding:16}}>No data for {key}</div>;

    const { title = {eng:'', fin:''}, year, category, runtime, synopsis = '', image = '' } = movieData;

    return (
      <div className={classes['movie-editor']}>
        <Spinner active={this.state.loading} />
        <ImageBrowser
          active={!!this.state.imageBrowser}                 // fix: required prop
          onSelect={this.onSelectImage}                      // fix: required prop
          onDeactivate={this.deactivateImageBrowser}         // fix: required prop
          selectedImage={image || ''}
          token={this.props.token} />
        <form ref="form" className="flex-item">
          <input type="hidden" name="_key" value={key || ''} />
          <input type="hidden" name="image" value={image} />
          <div className={classes['image-div']}>
            <div className={classes['image-wrapper']} onClick={this.activateImageBrowser}>
              <MovieThumbnail image={imageData?.thumb || ''} stock={!!imageData?.thumb && !imageData?.full} />
              <div className={classes['overlay']}>
                Choose Image
              </div>
            </div>
          </div>
          {/* ...existing form inputs... */}
          {/* make sure EditorSection props exist */}
          <div className={classes['form-input']}>
            <div className={classes['col']}>
              <EditorSection
                title="Title"
                value={{...title}}
                selectedLang={this.state.lang.title}
                onChangeLang={this.changeHandlers['lang']['title']}
                onChange={this.changeHandlers['input']['title']}
                type="text">
                <TextEditorSection />
              </EditorSection>
              <EditorSection
                title="Category"
                value={category}
                onChange={this.changeHandlers['input']['category']}
                type="select">
                <SelectEditorSection choices={this.state.categories} />
              </EditorSection>
              <EditorSection
                title="Year"
                value={year}
                onChange={this.changeHandlers['input']['year']}
                type="text">
                <TextEditorSection />
              </EditorSection>
              <EditorSection
                title="Runtime"
                value={runtime}
                onChange={this.changeHandlers['input']['runtime']}
                type="text">
                <TextEditorSection />
              </EditorSection>
              <EditorSection
                title="Synopsis"
                value={synopsis}
                onChange={this.changeHandlers['input']['synopsis']}
                type="textarea">
                <TextAreaEditorSection />
              </EditorSection>
            </div>
            <div className={classes['col']}>
              <div className={classes['people']}>
                <div className={classes['cast']}>
                  <InputList
                    key="cast"
                    name="Cast"
                    data={(movieData.people || {}).cast || []}
                    keys={['name']}
                    onChange={this.changeHandlers['people']['cast']} />
                </div>
                <div className={classes['crew']}>
                  <InputList
                    key="crew"
                    name="Crew"
                    data={(movieData.people || {}).crew || []}
                    keys={['name', 'job']}
                    onChange={this.changeHandlers['people']['crew']} />
                </div>
              </div>
            </div>
          </div>
          <Control onSubmit={this.onSubmit} onCancel={this.onCancel} />
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  token: state.auth?.token,
  production: state.production // adjust to your reducer shape
});

export default connect(mapStateToProps)(withRouter(MovieEditor));
